import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * The organization's talk rates as a short table — plan-017 WAPP-A (design §29.11 rows 1 and 4; owner answers OQ-1,
 * Q1, Q10, PQ-5). Drawn on the Dashboard (under the plan card) and on Billing (with the upcoming changes listed).
 *
 * One line per kind — the glossary's customer copy, the rate a minute and an hour, and whose price it is ("Your price"
 * on the organization's own rate, "Your plan's price" on its plan's rate — plan-020 PIN, design §32.12 Q-27 —
 * "Standard price" otherwise) — or ONE line while every kind costs the same (Q1). Then
 * "Tests are free." (owner answer Q4, PQ-5: a test uses no money, no trial and no included minutes). Every figure is the
 * API's (`useTalkRates`); a failed read says so and shows none, never a remembered or a typed-in figure.
 */
import type { TalkRateSource } from "@wetalk/domain";
import { Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Tag } from "@wetalk/ui";
import type { ReactNode } from "react";

import { formatMoney, formatWholeMoney } from "../../money.js";
import { talkRateLinesOf, upcomingTalkRateChangeText, useTalkRates, type TalkRateSet } from "../../api/slice/talk-rate.js";

import "./talk-rate-panel.css";

/** The table itself, for a set already read. */
export function TalkRateTable({ set }: { readonly set: TalkRateSet }): ReactNode {
  usePortalTranslation("core");
  const line = talkRateLinesOf(set.talkRates);
  if (line.length === 0) return <p className="c-note">{portalText("core", "talk_rate.none")}</p>;
  return (
    // A scroll container when the rates are wider than a phone: focusable and named, so a keyboard can scroll it
    // (axe `scrollable-region-focusable`, plan-020 E2E at 320 px).
    <div className="tr-table" role="region" aria-label={portalText("core", "talk_rate.caption")} tabIndex={0}>
      <Table caption={portalText("core", "talk_rate.caption")}>
        <TableHead>
          <TableRow>
            <TableHeaderCell>{portalText("core", "talk_rate.column.kind")}</TableHeaderCell>
            <TableHeaderCell numeric>{portalText("core", "talk_rate.column.per_minute")}</TableHeaderCell>
            <TableHeaderCell numeric>{portalText("core", "talk_rate.column.per_hour")}</TableHeaderCell>
            <TableHeaderCell>
              <span className="c-visually-hidden">{portalText("core", "talk_rate.column.source")}</span>
            </TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {line.map((one) => (
            <TableRow key={one.kind.join(",")}>
              <TableCell>{one.label}</TableCell>
              <TableCell numeric>{formatMoney(one.perMinute)}</TableCell>
              <TableCell numeric>{formatWholeMoney(one.perHour)}</TableCell>
              <TableCell>
                <TalkRateSourceLabel source={one.source} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/** Whose price a line is: the organization's own, its plan's, or the standard. */
function TalkRateSourceLabel({ source }: { readonly source: TalkRateSource }): ReactNode {
  switch (source) {
    case "account":
      return <Tag tone="accent">{portalText("core", "talk_rate.your_price")}</Tag>;
    case "offer":
      return <Tag tone="accent">{portalText("core", "talk_rate.offer_price")}</Tag>;
    case "standard":
      return <span className="tr-standard">{portalText("core", "talk_rate.standard_price")}</span>;
  }
}

export interface TalkRatePanelProps {
  /**
   * `dashboard` — the table, "Tests are free." and each upcoming change as one line under it;
   * `billing` — the same table with the charging note, then "Upcoming changes" listed (or that none is scheduled).
   */
  readonly variant: "dashboard" | "billing";
}

/** The organization's rates, read through the one hook, with their loading and failure states. */
export function TalkRatePanel({ variant }: TalkRatePanelProps): ReactNode {
  usePortalTranslation("core");
  const rates = useTalkRates();
  if (rates.isPending) return <p className="c-note">{portalText("core", "talk_rate.loading")}</p>;
  if (rates.data === undefined) return <p className="c-note" role="status">{portalText("core", "talk_rate.unavailable")}</p>;
  const set = rates.data;
  return (
    <div className="tr-panel">
      <TalkRateTable set={set} />
      {variant === "billing" ? <p className="tr-note">{portalText("core", "talk_rate.charged_note")}</p> : null}
      <p className="tr-note tr-free">{portalText("core", "talk_rate.tests_are_free")}</p>
      {variant === "dashboard" ? (
        set.upcoming.length === 0 ? null : (
          <ul className="tr-upcoming" aria-label={portalText("core", "dashboard.upcoming_title")}>
            {set.upcoming.map((change) => (
              <li key={`${change.kind}-${change.startsAt.toISOString()}`}>{upcomingTalkRateChangeText(change)}</li>
            ))}
          </ul>
        )
      ) : (
        <>
          <p className="tr-upcoming-title">{portalText("finance", "billing.rates.upcoming_title")}</p>
          {set.upcoming.length === 0 ? (
            <p className="tr-note">{portalText("finance", "billing.rates.no_upcoming")}</p>
          ) : (
            <ul className="tr-upcoming" aria-label={portalText("finance", "billing.rates.upcoming_title")}>
              {set.upcoming.map((change) => (
                <li key={`${change.kind}-${change.startsAt.toISOString()}`}>{upcomingTalkRateChangeText(change)}</li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
